import type { Unit, UnitList } from "shelving";
import { AllowSchema, LENGTH_UNITS, MASS_UNITS, PERCENT_UNITS, SPEED_UNITS, TIME_UNITS, VOLUME_UNITS, getMapItem, isProp, isString } from "shelving";
import { formatCompactQuantity } from "shared/util/number";

/** Types of units we support. */
export type UnitType = "percent" | "time" | "distance" | "speed" | "weight" | "volume" | "length";

/** Schema for the units of a specific unit type. */
export class UnitSchema<K extends string> extends AllowSchema<K, Unit<K>> {
	public declare readonly title: string;
	public declare readonly allow: UnitList<K>;
	public declare readonly value: K;
	public readonly main: Unit<K>;
	constructor(props: { title: string; allow: UnitList<K>; value: K }) {
		super(props);
		this.main = getMapItem(this.allow, this.value);
	}
}

/** List of schema types. */
export const UNIT_TYPES: {
	readonly [K in UnitType]: UnitSchema<string>;
} = {
	percent: new UnitSchema({ title: "Percent", allow: PERCENT_UNITS, value: "percent" }),
	time: new UnitSchema({ title: "Time", allow: TIME_UNITS, value: "second" }),
	distance: new UnitSchema({ title: "Distance", allow: LENGTH_UNITS, value: "kilometer" }),
	speed: new UnitSchema({ title: "Speed", allow: SPEED_UNITS, value: "meter-per-second" }),
	weight: new UnitSchema({ title: "Weight", allow: MASS_UNITS, value: "kilogram" }),
	length: new UnitSchema({ title: "Length", allow: LENGTH_UNITS, value: "centimeter" }),
	volume: new UnitSchema({ title: "Volume", allow: VOLUME_UNITS, value: "milliliter" }),
};

/** Is an unknown value a valid unit type. */
export const isUnitType = (type: unknown): type is UnitType => isString(type) && isProp(UNIT_TYPES, type);

/** Find a `Unit` instance for a unit type (e.g. `distance`) and unit key (e.g. `kilometer`), or throw `RequiredError` if `unit` is `null` or unknown. */
export function getUnit(type: UnitType, units: string): Unit<string> {
	return UNIT_TYPES[type].allow.getUnit(units);
}

/** Find a `Unit` instance for a unit type (e.g. `distance`) and unit key (e.g. `kilometer`), or return the default unit if `unit` is `null` or unknown. */
export function getMainUnit(type: UnitType, units: string | null): Unit<string> {
	const { allow, main } = UNIT_TYPES[type];
	return units && allow.has(units) ? allow.getUnit(units) : main;
}

/**
 * Convert an amount from a source unit to a target unit
 * - `null` or `undefined` units are assumed to be default unit for unit type.
 */
export function convertUnitAmount(amount: number, type: UnitType, sourceUnits: string, targetUnits: string): number {
	return getUnit(type, sourceUnits).to(amount, targetUnits ?? undefined);
}

/** Cram a numeric amount with a unit, e.g. `12.9K mi` or `192K km` */
export function formatUnitAmount(amount: number, type: UnitType, targetUnits: string): string {
	const targetUnit = getUnit(type, targetUnits);
	return formatCompactQuantity(amount, targetUnit.abbr);
}

/** Convert and format an amount with units by supplying a type and source/target unit, or format as a number if `type` or `unit` are unknown. */
export function formatUnitAmountAs(amount: number, type: UnitType, sourceUnits: string, targetUnits: string): string {
	const targetUnit = getUnit(type, targetUnits);
	return formatCompactQuantity(targetUnit.from(amount, sourceUnits), targetUnit.abbr);
}
